import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { API_URL } from '../../store'
import MarketingLayout from './components/MarketingLayout'
import { trackCompleteRegistration } from '../../utils/metaPixel'

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
}

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
}

const initialForm = {
  name: '',
  phone: '',
  zipCode: '',
  smsConsent: false,
}

const initialAttribution = {
  referredByCode: null,
  utmSource: null,
  utmMedium: null,
  utmCampaign: null,
  utmContent: null,
  utmTerm: null,
}

const onlyDigits = (value) => String(value || '').replace(/\D/g, '')

function WaitlistPage() {
  const location = useLocation()
  const [form, setForm] = useState(initialForm)
  const [attribution, setAttribution] = useState(initialAttribution)
  const [errors, setErrors] = useState({})
  const [submitError, setSubmitError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [successData, setSuccessData] = useState(null)
  const [copyState, setCopyState] = useState('idle')

  useEffect(() => {
    document.title = 'SOS SMS - Founding Member Waitlist'
    document.querySelector('meta[name="description"]')?.setAttribute(
      'content',
      'Join the SOS SMS founding member waitlist. One text connects you to a licensed, insured contractor fast - no app, no account, no chasing quotes.'
    )
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(location.search)

    setAttribution({
      referredByCode: params.get('ref') || null,
      utmSource: params.get('utm_source') || null,
      utmMedium: params.get('utm_medium') || null,
      utmCampaign: params.get('utm_campaign') || null,
      utmContent: params.get('utm_content') || null,
      utmTerm: params.get('utm_term') || null,
    })
  }, [location.search])

  const validateForm = () => {
    const nextErrors = {}
    const phoneDigits = onlyDigits(form.phone)
    const zipDigits = onlyDigits(form.zipCode)

    if (!form.name.trim()) {
      nextErrors.name = 'Full name is required.'
    }

    if (!form.phone.trim()) {
      nextErrors.phone = 'Mobile number is required.'
    } else if (phoneDigits.length < 10) {
      nextErrors.phone = 'Enter a valid mobile number with at least 10 digits.'
    }

    if (!form.zipCode.trim()) {
      nextErrors.zipCode = 'Metro/ZIP is required.'
    } else if (zipDigits.length !== 5) {
      nextErrors.zipCode = 'Enter a valid 5-digit ZIP code.'
    }

    return nextErrors
  }

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target

    setForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }))

    setErrors((current) => ({ ...current, [name]: undefined }))
    setSubmitError('')
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    const nextErrors = validateForm()
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return
    }

    setIsSubmitting(true)
    setSubmitError('')

    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        zipCode: onlyDigits(form.zipCode),
        smsConsent: form.smsConsent,
        referredByCode: attribution.referredByCode,
        utmSource: attribution.utmSource,
        utmMedium: attribution.utmMedium,
        utmCampaign: attribution.utmCampaign,
        utmContent: attribution.utmContent,
        utmTerm: attribution.utmTerm,
      }

      const res = await axios.post(`${API_URL}api/waitlist`, payload)

      if (res.data?.success === true) {
        setSuccessData(res.data.data)
        trackCompleteRegistration()
      } else {
        setSubmitError('Something went wrong. Please try again.')
      }
    } catch (error) {
      if (error.response?.status === 409) {
        setSubmitError(
          error.response?.data?.message || 'This phone number is already on the waitlist.'
        )
      } else {
        setSubmitError(
          error.response?.data?.message || 'Failed to join the waitlist. Please try again.'
        )
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCopy = async () => {
    if (!successData?.referralLink) {
      return
    }

    try {
      await navigator.clipboard.writeText(successData.referralLink)
      setCopyState('copied')
      window.setTimeout(() => setCopyState('idle'), 2000)
    } catch (error) {
      setCopyState('failed')
      window.setTimeout(() => setCopyState('idle'), 2000)
    }
  }

  return (
    <MarketingLayout>
      <section className="relative overflow-hidden bg-gradient-to-br from-green-900 via-green-800 to-emerald-700 text-white">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="waitlist-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#waitlist-grid)" />
          </svg>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 text-center">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
            <motion.div
              variants={fadeUp}
              className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm font-medium mb-6 backdrop-blur-sm"
            >
              <span className="w-2 h-2 rounded-full bg-green-300 animate-pulse"></span>
              Founding Member Waitlist
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6"
            >
              One text.
              <br />
              <span className="text-green-300">Help on the way.</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="text-lg text-green-100 leading-relaxed max-w-3xl mx-auto"
            >
              Join the founding member waitlist for an SMS-first home emergency membership
              that helps you reach a licensed, insured contractor without downloading an app.
            </motion.p>

            <motion.div variants={fadeUp} className="mt-8 flex justify-center">
              <a
                href="#waitlist-form"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-green-800 font-bold rounded-2xl hover:bg-green-50 transition-colors shadow-lg text-base scroll-smooth"
                style={{ scrollBehavior: 'smooth' }}
              >
                Join the founding member waitlist
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
            </motion.div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" className="w-full h-12 fill-white" preserveAspectRatio="none">
            <path d="M0,32 C360,60 1080,0 1440,32 L1440,60 L0,60 Z" />
          </svg>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.p variants={fadeUp} className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
              What It Is
            </motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              SOS SMS is a membership for the moment you need help now
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-3xl mx-auto">
              When plumbing, electrical, or HVAC problems hit, you text SOS SMS and we work
              to connect you with a licensed, insured contractor near you. No marketplace
              browsing. No app account to set up first.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.p variants={fadeUp} className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
              The One Text
            </motion.p>
            <motion.p variants={fadeUp} className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-4">
              Text one number. We find a licensed, insured contractor near you and get them moving - no app, no account, no calling down a list of people who do not pick up.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-14"
          >
            <motion.p variants={fadeUp} className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
              The Guarantee
            </motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              Clear commitment before anyone rolls
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto">
              We designed the model to reduce ghosting, reduce wasted time, and make the
              dispatch step feel dependable for both sides.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto"
          >
            <motion.div
              variants={fadeUp}
              className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-1 transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3v-6m-3 6v-9m9-4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">Licensed and insured pros only</h3>
              <p className="text-gray-500 leading-relaxed">
                The goal is simple: when you reach out, we work to route your request to a
                real contractor who is properly insured and ready to respond.
              </p>
            </motion.div>

            <motion.div
              variants={fadeUp}
              className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-1 transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">No dispatch, no trip charge</h3>
              <p className="text-gray-500 leading-relaxed">
                The trip charge is secured before dispatch so the contractor knows the call is
                real, and if no one is dispatched there is no trip charge to collect.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-b from-gray-50/60 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.p variants={fadeUp} className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
              Pricing
            </motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              What it costs, with nothing buried
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="relative grid md:grid-cols-[1fr_auto_1fr] gap-6 md:gap-0 items-stretch max-w-4xl mx-auto"
          >
            <motion.div
              variants={fadeUp}
              className="bg-white rounded-2xl p-8 border border-gray-200 flex flex-col"
            >
              <div className="min-h-[176px]">
                <div className="w-11 h-11 rounded-xl bg-gray-100 text-gray-500 flex items-center justify-center mb-6">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>

                <div className="text-sm font-semibold uppercase tracking-wider text-primary mb-3">Membership</div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-5xl font-extrabold text-gray-900 tracking-tight">$14</span>
                  <span className="text-base font-medium text-gray-500">/month</span>
                </div>
                <div className="text-sm text-gray-500">or $99/year ($8.25/month). Cancel anytime.</div>
              </div>

              <p className="text-sm text-gray-700 leading-relaxed mt-6 pt-6 border-t border-gray-100">
                Your membership buys the network, the response guarantee, and escrow protection on every job.
              </p>
            </motion.div>

            <motion.div variants={fadeUp} className="flex md:flex-col items-center justify-center gap-0 py-2 md:py-0 md:px-5">
              <div className="hidden md:block flex-1 w-px bg-gray-200" />
              <span className="w-8 h-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary text-base font-semibold shrink-0 my-2 md:my-3">
                +
              </span>
              <div className="hidden md:block flex-1 w-px bg-gray-200" />
            </motion.div>

            <motion.div
              variants={fadeUp}
              className="bg-white rounded-2xl p-8 border-2 border-primary/25 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_8px_24px_-8px_rgba(16,90,60,0.12)] flex flex-col"
            >
              <div className="min-h-[176px]">
                <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
                  </svg>
                </div>

                <div className="text-sm font-semibold uppercase tracking-wider text-primary mb-3 leading-snug">
                  $150 trip charge, only when you actually need someone
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-5xl font-extrabold text-gray-900 tracking-tight">$150</span>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-100 space-y-4">
                <p className="text-sm text-gray-700 leading-relaxed">
                  When you send a request and we dispatch a contractor, $150 is held to get a licensed pro to your door.
                </p>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold px-3 py-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  No request, no charge. Ever.
                </span>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mt-10 max-w-4xl mx-auto flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 px-6 py-4"
          >
            <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <svg className="w-3.5 h-3.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
            <p className="text-sm text-gray-600 leading-relaxed">
              The work itself is between you and your contractor. Parts, labor, scope, and
              price are agreed directly with the pro and billed directly by them. SOS SMS
              takes no percentage of the job.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.p variants={fadeUp} className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
              Join the Waitlist
            </motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              Founding members get first access
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto">
              Tell us where to reach you and we&apos;ll save your spot.
            </motion.p>
          </motion.div>

          <motion.div
            id="waitlist-form"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="bg-gray-50 border border-gray-200 rounded-3xl p-6 sm:p-8 shadow-sm"
          >
            {successData ? (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-3">
                    Thank you for joining the founding member waitlist!
                  </h3>
                  <p className="text-gray-500">
                    You&apos;re #{successData.positionNumber} on the founding member waitlist
                  </p>
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="rounded-2xl border border-gray-200 bg-white p-4 text-center">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                      Your Position
                    </div>
                    <div className="text-3xl font-extrabold text-gray-900">
                      #{successData.positionNumber}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 bg-white p-4 text-center">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                      Referral Code
                    </div>
                    <div className="text-lg font-bold text-gray-900">
                      {successData.referralCode}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 bg-white p-4 text-center">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                      Founding Member Perk
                    </div>
                    <div className="text-sm font-medium text-gray-700">
                      Extra visibility for future referral and sweepstakes announcements
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
                  <div className="text-sm font-semibold text-gray-900 mb-2">Your referral link</div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 rounded-2xl bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-600 break-all">
                      {successData.referralLink}
                    </div>
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="inline-flex items-center justify-center px-5 py-3 bg-primary text-white font-semibold rounded-2xl hover:bg-green-700 transition-colors"
                    >
                      {copyState === 'copied' ? 'Copied' : 'Copy link'}
                    </button>
                  </div>
                  {copyState === 'failed' && (
                    <p className="mt-3 text-sm text-red-600">
                      Could not copy automatically. You can still copy the link manually.
                    </p>
                  )}
                </div>

                <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-4 text-sm text-emerald-900">
                  We may highlight engaged founding members for future referral perks or
                  sweepstakes-style promotions. Specific terms, timing, and eligibility will be
                  announced separately if offered.
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {submitError && (
                  <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {submitError}
                  </div>
                )}

                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                    Full name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-gray-900 outline-none focus:border-primary"
                    placeholder="Your full name"
                    required
                  />
                  {errors.name && <p className="mt-2 text-sm text-red-600">{errors.name}</p>}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                    Mobile number
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-gray-900 outline-none focus:border-primary"
                    placeholder="(555) 555-5555"
                    required
                  />
                  {errors.phone && <p className="mt-2 text-sm text-red-600">{errors.phone}</p>}
                </div>

                <div>
                  <label htmlFor="zipCode" className="block text-sm font-semibold text-gray-900 mb-2">
                    Metro/ZIP
                  </label>
                  <input
                    id="zipCode"
                    name="zipCode"
                    type="text"
                    value={form.zipCode}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-gray-900 outline-none focus:border-primary"
                    placeholder="33101"
                    required
                  />
                  {errors.zipCode && <p className="mt-2 text-sm text-red-600">{errors.zipCode}</p>}
                </div>

                <label className="flex items-start gap-3 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    name="smsConsent"
                    checked={form.smsConsent}
                    onChange={handleChange}
                    className="mt-1"
                  />
                  <span>
                    I agree to receive text messages from SOS SMS about my membership and service
                    requests. Message frequency varies. Msg & data rates may apply. Reply STOP
                    to opt out or HELP for help. See our{' '}
                    <Link to="/privacy-policy" className="text-primary underline">Privacy Policy</Link>{' '}
                    and <Link to="/terms" className="text-primary underline">Terms of Service</Link>.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full inline-flex items-center justify-center px-8 py-4 bg-primary text-white font-bold rounded-2xl hover:bg-green-700 transition-colors shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Saving your spot...' : 'Join the founding member waitlist'}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </MarketingLayout>
  )
}

export default WaitlistPage
